import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "@/lib/api";
import { defaultPageState } from "@/lib/pageViews";
import { toast } from "sonner";

// Reusable per-page view state: persists column/view/density prefs per user
// (via /api/preferences) and manages personal + shared Saved Views.
export function usePageView(pageDef) {
  const pageKey = pageDef.key;
  const [state, setState] = useState(() => defaultPageState(pageDef));
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const prefsRef = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const [prefs, vlist, appr] = await Promise.all([
          apiFetch("/preferences"),
          apiFetch(`/saved-views?page=${pageKey}`),
          apiFetch("/appearance"),
        ]);
        prefsRef.current = prefs || {};
        setIsAdmin(!!appr?.is_admin);
        setViews(vlist || []);
        const saved = prefs?.page_views?.[pageKey];
        if (saved) setState((s) => ({ ...s, ...saved }));
      } catch (e) { /* keep defaults */ } finally { setLoaded(true); }
    })();
  }, [pageKey]);

  // debounced persistence of per-page prefs
  const persistTimer = useRef(null);
  const flushPrefs = useCallback((next) => {
    clearTimeout(persistTimer.current);
    const page_views = { ...(prefsRef.current.page_views || {}), [pageKey]: next };
    prefsRef.current = { ...prefsRef.current, page_views };
    apiFetch("/preferences", { method: "PUT", body: JSON.stringify({ patch: { page_views } }) }).catch(() => {});
  }, [pageKey]);
  const persistPrefs = useCallback((next) => {
    clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => flushPrefs(next), 500);
  }, [flushPrefs]);
  useEffect(() => () => clearTimeout(persistTimer.current), []);

  const update = useCallback((patch) => {
    setState((s) => {
      const next = typeof patch === "function" ? patch(s) : { ...s, ...patch };
      persistPrefs(next);
      return next;
    });
    setActiveViewId(null);
  }, [persistPrefs]);

  const reloadViews = useCallback(async () => {
    setViews(await apiFetch(`/saved-views?page=${pageKey}`));
  }, [pageKey]);

  const applyView = useCallback((v) => {
    if (!v) return;
    setState((s) => ({ ...s, ...(v.config || {}) }));
    setActiveViewId(v.id);
    flushPrefs({ ...defaultPageState(pageDef), ...(v.config || {}) });
  }, [pageDef, flushPrefs]);

  const saveView = useCallback(async (name, shared) => {
    try {
      await apiFetch("/saved-views", {
        method: "POST",
        body: JSON.stringify({ page_key: pageKey, name, config: state, is_shared: !!shared }),
      });
      await reloadViews();
      toast.success(`Saved view “${name}”`);
    } catch (e) { toast.error(e.message); }
  }, [pageKey, state, reloadViews]);

  const deleteView = useCallback(async (id) => {
    try { await apiFetch(`/saved-views/${id}`, { method: "DELETE" }); await reloadViews(); if (activeViewId === id) setActiveViewId(null); toast.success("View deleted"); }
    catch (e) { toast.error(e.message); }
  }, [reloadViews, activeViewId]);

  const resetView = useCallback(() => { update(defaultPageState(pageDef)); }, [pageDef, update]);

  return {
    state, update, views, activeViewId, applyView, saveView, deleteView, resetView,
    isAdmin, loaded,
  };
}
